import React, { useCallback, useState } from 'react';
import {
  Container,
  Header,
  Button,
  Table,
  Box,
  SpaceBetween,
  ButtonDropdown,
} from '@cloudscape-design/components';
import { QuestionFormParams } from '../QuestionFormWizard';
import { FileData } from '../../textEditor/TextEditor';
import { SubQuestion } from 'src/types/helios/Question';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import SubQuestionSidePanel from '../SubQuestionSidePanel';
import WarningModal from '../../WarningModal';
import Tooltip from 'src/components/Tooltip';
import { AuditTooltipMessages } from 'src/contstant/commonLabels';
import { FileMetadata } from '../../FileDisplay';

interface SubQuestionsStepProps {
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  fileList: Record<string, FileData[]>;
  setFileList: React.Dispatch<React.SetStateAction<Record<string, FileData[]>>>;
  documentFiles?: Record<string, File[]>;
  setDocumentFiles?: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
  documentFilesMetadata?: Record<string, FileMetadata[]>;
  setDocumentFilesMetadata?: React.Dispatch<
    React.SetStateAction<Record<string, FileMetadata[]>>
  >;
}

const SubQuestions: React.FC<SubQuestionsStepProps> = ({
  formData,
  setFormData,
  fileList,
  setFileList,
  documentFiles,
  setDocumentFiles,
  documentFilesMetadata,
  setDocumentFilesMetadata,
}) => {
  // Split panel context
  const { setContent, setTitle, closeView } = useSplitPanel();

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);

  // Handle opening add side panel
  const handleOpenAddSidePanel = useCallback(() => {
    setTitle('Add Sub Question');
    setContent(
      <SubQuestionSidePanel
        mode="add"
        formData={formData}
        setFormData={setFormData}
        fileList={fileList}
        setFileList={setFileList}
        documentFiles={documentFiles}
        setDocumentFiles={setDocumentFiles}
        documentFilesMetadata={documentFilesMetadata}
        setDocumentFilesMetadata={setDocumentFilesMetadata}
        onClose={closeView}
      />,
    );
  }, [
    formData,
    setFormData,
    fileList,
    setFileList,
    documentFiles,
    setDocumentFiles,
    documentFilesMetadata,
    setDocumentFilesMetadata,
    setTitle,
    setContent,
    closeView,
  ]);

  // Handle opening edit side panel
  const handleOpenEditSidePanel = useCallback(
    (index: number) => {
      setTitle('Edit Sub Question');
      setContent(
        <SubQuestionSidePanel
          mode="edit"
          editingIndex={index}
          formData={formData}
          setFormData={setFormData}
          fileList={fileList}
          setFileList={setFileList}
          documentFiles={documentFiles}
          setDocumentFiles={setDocumentFiles}
          documentFilesMetadata={documentFilesMetadata}
          setDocumentFilesMetadata={setDocumentFilesMetadata}
          onClose={closeView}
        />,
      );
    },
    [
      formData,
      setFormData,
      fileList,
      setFileList,
      documentFiles,
      setDocumentFiles,
      documentFilesMetadata,
      setDocumentFilesMetadata,
      setTitle,
      setContent,
      closeView,
    ],
  );

  // Handle delete confirmation
  const handleDeleteClick = useCallback((index: number) => {
    setDeleteIndex(index);
    setShowDeleteModal(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(() => {
    if (deleteIndex >= 0 && formData.subQuestions) {
      const updatedSubQuestions = formData.subQuestions.filter(
        (_, index) => index !== deleteIndex,
      );
      setFormData((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
    }
    setShowDeleteModal(false);
    setDeleteIndex(-1);
    closeView();
  }, [deleteIndex, formData.subQuestions, setFormData, closeView]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteIndex(-1);
  }, []);

  return (
    <Container
      data-testid="sub-questions-container"
      header={
        <Header
          data-testid="sub-questions-header"
          actions={
            <Button
              data-testid="add-sub-question-button"
              iconName="add-plus"
              onClick={handleOpenAddSidePanel}
            >
              Add Sub Question
            </Button>
          }
          info={<Tooltip content={AuditTooltipMessages.SUB_QUESTION} />}
          variant="h3"
        >
          Sub question & Sub Task
        </Header>
      }
    >
      {formData.subQuestions && formData.subQuestions.length > 0 ? (
        <Table
          data-testid="sub-questions-table"
          variant="borderless"
          columnDefinitions={[
            {
              id: 'title',
              header: 'Sub-question Title',
              maxWidth: 700,
              cell: (item: SubQuestion) => item.title || '-',
            },
            {
              id: 'hasTask',
              header: 'Has Task',
              maxWidth: 100,
              cell: (item: SubQuestion) => (item.taskDetails ? 'Yes' : 'No'),
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item: SubQuestion) => {
                const index =
                  formData.subQuestions?.findIndex(
                    (q) => q.title === item.title && q.instruction === item.instruction,
                  ) ?? -1;
                return (
                  <ButtonDropdown
                    data-testid="action-button-dropdown"
                    expandToViewport={true}
                    items={[
                      {
                        text: 'Edit',
                        id: 'edit',
                      },
                      {
                        text: 'Delete',
                        id: 'delete',
                      },
                    ]}
                    variant="icon"
                    onItemClick={({ detail }) => {
                      if (detail.id === 'edit') {
                        handleOpenEditSidePanel(index);
                      }
                      if (detail.id === 'delete') {
                        handleDeleteClick(index);
                      }
                    }}
                  />
                );
              },
            },
          ]}
          items={formData.subQuestions}
          trackBy={(item: SubQuestion) => `${item.title}-${item.orderIndex}`}
          empty={
            <Box
              data-testid="empty-sub-questions-message"
              textAlign="center"
              color="text-status-inactive"
              padding={{ vertical: 'xs' }}
            >
              No sub questions added
            </Box>
          }
        />
      ) : (
        <Box
          data-testid="no-sub-questions-message"
          textAlign="center"
          color="text-status-inactive"
          padding={{ vertical: 'l' }}
        >
          No sub questions added
        </Box>
      )}

      <WarningModal
        data-testid="delete-sub-question-modal"
        visible={showDeleteModal}
        title="Delete Sub Question"
        message="Are you sure you want to delete this sub question? "
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Container>
  );
};

export default SubQuestions;
